import { AttendanceRecord, AttendanceSheet } from '../types';
import { databases, ID, ensureAuth } from './appwriteClient';
import { Query } from 'appwrite';
import { Permission, Role } from 'appwrite';
import { getCurrentUser } from './authService';

const DATABASE_ID = '682acd26001f69683cd8';
const ATTENDANCE_COLLECTION_ID = '683078a400301e04d0e8';
const ATTENDANCE_ALERTS_COLLECTION_ID = 'attendance-alerts';

// Function to create attendance alert for absent students
export const createAttendanceAlert = async (studentId: string, date: string): Promise<void> => {
  try {
    // Format the document ID as date-student_id (e.g., "06-08-2025-223123dbf123")
    const documentId = `${date}-${studentId}`;

    await databases.createDocument(
      DATABASE_ID,
      ATTENDANCE_ALERTS_COLLECTION_ID,
      documentId,
      {
        time: new Date().toISOString(),
        student_id: studentId
      },
      [
        Permission.read(Role.any()),
        Permission.write(Role.any())
      ]
    );

    console.log(`Attendance alert created for student ${studentId} on ${date}`);
  } catch (error: any) {
    // If document already exists (409 conflict), that's okay - alert was already created
    if (error?.code === 409) {
      console.log(`Attendance alert already exists for student ${studentId} on ${date}`);
    } else {
      console.error(`Error creating attendance alert for student ${studentId} on ${date}:`, error);
      throw error; // Re-throw non-duplicate errors
    }
  }
};

// Get attendance records for a specific date
export const getAttendanceByDate = async (date: string): Promise<AttendanceRecord[]> => {
  try {
    await ensureAuth();
    
    const response = await databases.listDocuments(
      DATABASE_ID,
      ATTENDANCE_COLLECTION_ID,
      [
        Query.equal('date', date)
      ]
    );

    return response.documents.map(doc => ({
      id: doc.$id,
      studentId: doc.student_id,
      status: doc.Status, // Use capital 'Status' as per Appwrite schema
      markedBy: doc.marked_by || 'system',
      date: doc.date
    }));
  } catch (error) {
    console.error('Error fetching attendance:', error);
    throw new Error('Failed to fetch attendance records');
  }
};

// Get attendance sheet for a specific date and class
export const getAttendanceSheet = async (
  _date: string,
  _classId: string
): Promise<AttendanceSheet | null> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  // In a real app, this would fetch from the database
  return null; // For now, we'll return null to indicate no existing attendance sheet
};

// Get attendance statistics for a class
export const getAttendanceStats = async (
  _classId: string,
  startDate: string,
  endDate: string
): Promise<{
  totalStudents: number;
  averageAttendance: number;
  absentCount: number;
  lateCount: number;
}> => {
  try {
    await ensureAuth();
    // Get all students
    const studentsResponse = await databases.listDocuments(
      DATABASE_ID,
      '682acd33000e34dd93c0' // STUDENTS_COLLECTION_ID
    );

    const totalStudents = studentsResponse.documents.length;

    // Get attendance records for the date range
    const attendanceResponse = await databases.listDocuments(
      DATABASE_ID,
      ATTENDANCE_COLLECTION_ID,
      [
        Query.greaterThanEqual('date', startDate),
        Query.lessThanEqual('date', endDate)
      ]
    );

    // Create a map to store the most recent attendance for each student
    const latestAttendance = new Map();

    // Process attendance records to get the most recent one for each student
    attendanceResponse.documents.forEach((record: any) => {
      const existingRecord = latestAttendance.get(record.student_id);
      if (!existingRecord || new Date(record.date) > new Date(existingRecord.date)) {
        latestAttendance.set(record.student_id, record);
      }
    });

    // Count attendance statuses from the most recent records
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;

    latestAttendance.forEach((record: any) => {
      switch (record.status) {
        case 'present':
          presentCount++;
          break;
        case 'absent':
          absentCount++;
          break;
        case 'late':
          lateCount++;
          break;
      }
    });

    // Calculate average attendance
    const averageAttendance = totalStudents > 0 
      ? Math.round((presentCount / totalStudents) * 100)
      : 0;

    return {
      totalStudents,
      averageAttendance,
      absentCount,
      lateCount
    };
  } catch (error) {
    console.error('Error fetching attendance stats:', error);
    throw new Error('Failed to fetch attendance statistics');
  }
};

// Get attendance record by student ID and date
export const getAttendanceRecord = async (studentId: string, date: string): Promise<{ id: string, status: 'present' | 'absent' | 'late', remarks?: string } | null> => {
  try {
    // Validate inputs
    if (!studentId || !date) {
      console.error('Invalid studentId or date provided');
      return null;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      console.error('Invalid date format. Expected YYYY-MM-DD');
      return null;
    }

    await ensureAuth();

    const response = await databases.listDocuments(
      DATABASE_ID,
      ATTENDANCE_COLLECTION_ID,
      [
        Query.equal('student_id', studentId),
        Query.equal('date', date),
        Query.limit(1)
      ]
    );

    if (response.documents.length > 0) {
      const record = response.documents[0];
      return {
        id: record.$id,
        status: record.Status as 'present' | 'absent' | 'late',
        remarks: record.remarks || ''
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching attendance record:', error);
    throw error; // Re-throw to allow caller to handle
  }
};

// Update attendance record
export const updateAttendance = async (
  studentId: string,
  data: { status: 'present' | 'absent' | 'late', remarks?: string },
  date?: string
) => {
  try {
    // Validate inputs
    if (!studentId) {
      throw new Error('Student ID is required');
    }
    if (!data.status || !['present', 'absent', 'late'].includes(data.status)) {
      throw new Error('Valid status (present/absent/late) is required');
    }

    await ensureAuth();
    const currentUser = await getCurrentUser();
    const markedBy = currentUser?.id || 'system';

    // Use provided date or current date in YYYY-MM-DD format
    const targetDate = date || new Date().toISOString().split('T')[0];
    const currentTimestamp = new Date().toISOString();

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(targetDate)) {
      throw new Error('Invalid date format. Expected YYYY-MM-DD');
    }

    // Get the existing attendance record
    const existingRecord = await getAttendanceRecord(studentId, targetDate);

    if (!existingRecord) {
      // Create new attendance record
      await databases.createDocument(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        ID.unique(),
        {
          student_id: studentId,
          Status: data.status,
          date: targetDate,
          remarks: data.remarks || '',
          Created_at: currentTimestamp,
          Updated_at: currentTimestamp,
        },
        [
          Permission.read(Role.any()),
          Permission.update(Role.any()),
          Permission.delete(Role.any())
        ]
      );
      console.log(`Created attendance record for student ${studentId} on ${targetDate}: ${data.status} (marked by: ${markedBy})`);
    } else {
      // Update the existing attendance record
      await databases.updateDocument(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        existingRecord.id,
        {
          Status: data.status,
          remarks: data.remarks || '',
          Updated_at: currentTimestamp,
        }
      );
      console.log(`Updated attendance record for student ${studentId} on ${targetDate}: ${data.status} (marked by: ${markedBy})`);
    }

    // Create attendance alert if student is marked as absent
    // Only create alert if it's a new absence or status changed to absent
    if (data.status === 'absent') {
      try {
        await createAttendanceAlert(studentId, targetDate);
      } catch (alertError) {
        // Log but don't fail the entire operation if alert creation fails
        console.error('Failed to create attendance alert, but attendance was saved:', alertError);
      }
    }

    return { success: true, date: targetDate, status: data.status };
  } catch (error) {
    console.error('Error updating attendance:', error);
    throw error;
  }
}; 